import React, { useState, useRef, useEffect } from "react";
import { Box } from "@mui/material";

export default function CashAmountInput({ label = "", max = null, onChange = () => {} }) {
  const [amountInCents, setAmountInCents] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [showCursor, setShowCursor] = useState(true);
  const inputRef = useRef(null);
  const prevMaxRef = useRef(max);

  // Reset value if max changes
  useEffect(() => {
    if (prevMaxRef.current !== max) {
      setAmountInCents(0);
      onChange(0);
      prevMaxRef.current = max;
    }
  }, [max, onChange]);

  // Blink logic
  useEffect(() => {
    let interval = null;
    if (isFocused) {
      interval = setInterval(() => {
        setShowCursor((prev) => !prev);
      }, 500);
    } else {
      setShowCursor(true);
    }
    return () => clearInterval(interval);
  }, [isFocused]);

  // Handle focus/click
  const handleClick = () => {
    inputRef.current?.focus();
    if (!isFocused) setIsFocused(true);

    if (amountInCents === 0 && max != null) {
      const maxCents = Math.round(max * 100);
      setAmountInCents(maxCents);
      onChange(maxCents);
    }
  };

  const handleKeyDown = (event) => {
    const { key } = event;

    if (!/^\d$/.test(key)) {
      if (key === "Backspace") {
        setAmountInCents((prev) => {
          const next = Math.floor(prev / 10);
          onChange(next);
          return next;
        });
      }
      event.preventDefault();
      return;
    }

    const next = amountInCents * 10 + parseInt(key, 10);
    const maxCents = max != null ? Math.round(max * 100) : null;

    if (maxCents != null && next > maxCents) {
      event.preventDefault();
      return;
    }

    setAmountInCents(next);
    onChange(next);
    event.preventDefault();
  };

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  const euro = Math.floor(amountInCents / 100);
  const cents = (amountInCents % 100).toString().padStart(2, "0");
  const formattedEuro = euro.toLocaleString("de-DE");

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 2,
        pl: 3,
        pr: 3,
        borderRadius: 2,
        backgroundColor: "#f1f1f1",
        boxShadow: 3,
        fontFamily: "'Courier New', monospace",
        fontSize: "2rem",
        userSelect: "none",
        cursor: "text",
        minWidth: 360,
        outline: isFocused ? "2px solid #1976d2" : "none",
      }}
    >
      <span
        style={{
          fontSize: "2rem",
          color: "#888",
          textTransform: "uppercase",
          fontWeight: "bold",
          marginRight: "auto",
          lineHeight: "1",
        }}
      >
        {label}
      </span>

      <Box sx={{ display: "inline-flex", alignItems: "flex-end" }}>
        <span style={{ fontSize: "2rem" }}>{formattedEuro}</span>
        <span style={{ fontSize: "1.2rem", marginLeft: 4 }}>,{cents}&nbsp;</span>
        {isFocused && (
          <span
            style={{
              display: "inline-block",
              width: "2px",
              height: "2.4rem",
              backgroundColor: "#1976d2",
              marginLeft: 6,
              marginRight: 4,
              visibility: showCursor ? "visible" : "hidden",
            }}
          />
        )}
        <span style={{ fontSize: "1.5rem" }}>€</span>
      </Box>

      <input
        type="text"
        ref={inputRef}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        style={{
          position: "absolute",
          opacity: 0,
          width: 1,
          height: 1,
          pointerEvents: "none",
        }}
      />
    </Box>
  );
}
